import type { HomeCopy } from '../types/home';

export const homeRo: HomeCopy = {
  hero: {
    eyebrow: 'Pentru restaurante, cafenele și baruri',
    title: {
      line1: 'Meniul tău,',
      pre: 'pe un ',
      highlight: 'cod QR',
      post: ',',
      line3: 'în câteva minute.',
    },
    subtitle: {
      intro:
        'Clienții scanează un mic card de pe masă, iar meniul tău se deschide pe telefonul lor. ',
      easyToUse: 'Ușor de folosit',
      comma: ',',
      safeForGuests: 'sigur pentru clienți',
      and: 'și',
      kindToNature: 'prietenos cu natura',
      outro: '. Fără aplicație, fără retipărire, fără bătaie de cap.',
    },
    primaryCta: 'Începe gratuit - nu ai nevoie de card',
    secondaryCta: 'Vezi cum funcționează',
    checklist: ['Gata în ~15 minute', 'Funcționează pe orice telefon', 'Planul gratuit inclus'],
  },
  trustStrip: {
    ariaLabel: 'De ce aleg restaurantele SafestMenu',
    words: [
      'Ușor de folosit',
      'De încredere pentru clienți',
      'Sigur și igienic',
      'Prietenos cu natura',
      'Fără aplicație necesară',
      'Gratuit la început',
    ],
  },
  why: {
    eyebrow: 'De ce renunți la hârtie',
    heading: 'Meniurile pe hârtie costă bani, răspândesc germeni și ajung la gunoi.',
    cards: [
      {
        alt: 'O masă impecabilă de restaurant cu un mic suport pentru cod QR în locul meniurilor pe hârtie',
        title: 'Un meniu pe care nimeni altcineva nu l-a atins',
        text: {
          pre: 'Un meniu pe hârtie trece prin sute de mâini și aproape că nu este spălat niciodată. Cu un cod QR, fiecare client îl răsfoiește pe propriul telefon - cel mai',
          highlight: 'igienic meniu',
          post: 'care există.',
        },
      },
      {
        alt: 'Un proprietar de restaurant își actualizează meniul de pe telefon la pasul din bucătărie',
        title: 'Schimbă un preț în 30 de secunde',
        text: {
          pre: 'Preparat nou? Furnizorul a crescut prețurile? Editează de pe telefon și fiecare masă vede actualizarea instant. E cu adevărat',
          highlight: 'ușor de folosit',
          post: ' - dacă știi să scrii un mesaj text, știi să folosești SafestMenu.',
        },
      },
      {
        alt: 'Un teanc de meniuri uzate pe hârtie lângă un singur suport reutilizabil pentru cod QR și o plantă verde',
        title: 'Tipărește o dată, niciodată din nou',
        text: {
          pre: 'Fiecare sezon cu meniuri retipărite și laminate înseamnă bani și hârtie la gunoi. Un singur card mic pe masă este',
          highlight: 'prietenos cu natura',
          post: '- și cu bugetul tău.',
        },
      },
    ],
  },
  howItWorks: {
    eyebrow: 'Cum funcționează',
    heading: 'Trei pași. Nu ai nevoie de cunoștințe tehnice.',
    steps: [
      {
        title: 'Scrie-ți meniul',
        text: 'Adaugă preparatele și prețurile. Nu găsești cuvintele potrivite? AI-ul nostru scrie descrieri apetisante și generează fotografii chiar și pentru preparatele care nu au nicio poză.',
      },
      {
        title: 'Tipărește codul QR',
        text: 'Descarcă cartonașe de masă gata de tipărit, în toate dimensiunile uzuale. Așază-le pe mese, în vitrină, la tejghea - oriunde se așază clienții.',
      },
      {
        title: 'Clienții scanează și comandă',
        text: 'Fără nicio aplicație de instalat. Meniul se deschide direct în browserul telefonului - cu fotografii, informații despre alergeni și în limba lor.',
      },
    ],
  },
  scanImport: {
    eyebrow: 'Nou - importă în câteva minute',
    heading: 'Ai deja un meniu? Pornește de la el.',
    paragraph:
      'Nu trebuie să scrii totul de la zero. Încarcă un PDF sau câteva fotografii ale meniului tău tipărit, iar SafestMenu îl citește - preparate, prețuri, categorii - apoi îți oferă o ciornă curată, gata de verificat și publicat. Un meniu obișnuit încape în creditele gratuite primite la înregistrare.',
    steps: [
      {
        title: 'Încarcă meniul',
        text: 'Adaugă un PDF sau până la 40 de fotografii. Vezi costul exact dinainte - 2 credite pe pagină - înainte să fii taxat.',
      },
      {
        title: 'AI-ul citește fiecare pagină',
        text: 'Fiecare pagină devine preparate, prețuri și categorii editabile, cu limba meniului și moneda detectate automat.',
      },
      {
        title: 'Verifică și publică',
        text: 'Ajungi pe un ecran de verificare ordonat, corectezi ce mai trebuie ajustat, apoi creezi un meniu nou sau adaugi paginile la unul deja existent.',
      },
    ],
  },
  productTour: {
    eyebrow: 'Vezi-l în acțiune',
    heading: 'Privește cum prinde contur un meniu.',
    paragraph:
      'De la o pagină goală, direct pe masă. Acesta este editorul real - adaugi un preparat, lași AI-ul să scrie descrierea, tragi elementele în ordine și publici. Fără niciun apel demo necesar.',
  },
  aiStudio: {
    eyebrow: 'AI integrat',
    heading: {
      pre: 'Nu ai fotograf?',
      highlight: 'Nicio problemă.',
    },
    paragraph:
      'Fă o poză rapidă - sau nici măcar atât - și lasă SafestMenu să genereze imagini frumoase și consistente pentru preparate. Scrie și descrierile apetisante. Tu rămâi la control: alegi imaginea care îți place, o păstrezi, gata.',
    pills: [
      'Descrieri AI · 1 credit',
      'Fotografii AI pentru preparate · 5 credite',
      'Traduceri de meniu · 1 credit fiecare',
      'Scanare meniu · 2 credite/pagină',
      'Sugestii AI de alergeni · 1 credit',
      'Creditele nu expiră niciodată',
    ],
  },
  features: {
    eyebrow: 'Totul inclus',
    heading: 'Construit pentru proprietari de restaurante, nu pentru departamente IT.',
    sidebarCaption:
      'Fiecare vizualizare a unui preparat este numărată automat - ca să vezi ce își doresc clienții și să pui cele mai populare preparate în prim-plan.',
    items: [
      {
        title: 'Prețurile se actualizează instant',
        text: 'Editezi orice de pe telefon. Fiecare cod QR de pe fiecare masă arată mereu cel mai recent meniu.',
      },
      {
        title: 'Fotografii și descrieri AI',
        text: 'Nu ai fotograf? AI-ul integrat scrie descrieri care lasă gura apă și generează fotografii pentru preparatele tale.',
      },
      {
        title: 'Toți cei 14 alergeni UE',
        text: 'Marchezi alergenii pentru fiecare preparat, o singură dată. Clienții văd etichete clare la masă - fără ghicit, fără răspunsuri riscante.',
      },
      {
        title: 'Șase limbi incluse',
        text: 'Turiștii sunt și ei clienți. Meniul tău vorbește engleză, germană, spaniolă, franceză, italiană și română.',
      },
      {
        title: 'Traduce meniul dintr-un singur clic',
        text: 'AI-ul traduce fiecare preparat păstrând tonul propriu al meniului tău, câte un credit fiecare. Editezi un preparat mai târziu și doar acela este retradus - rămâi la zi pentru câțiva bani.',
      },
      {
        title: 'Meniuri care se schimbă după program',
        text: 'Ai carduri separate pentru mic dejun, prânz și cină. Setezi orele o singură dată - același cod QR arată mereu meniul pe care îl servești chiar acum.',
      },
      {
        title: 'Duplică orice meniu',
        text: 'Copiezi un meniu întreg, cu preparate și fotografii, ajustezi ce s-a schimbat și publici când ești gata. Util pentru meniuri sezoniere și pentru a doua locație.',
      },
      {
        title: 'Vezi ce își doresc clienții',
        text: 'Statistici simple arată ce preparate sunt cele mai vizualizate - ca să știi ce să pui pe tabla cu specialități.',
      },
      {
        title: 'Aspectul tău, brandul tău',
        text: 'Alegi culorile și fonturile, adaugi logo-ul și fotografiile preparatelor. Meniul tău arată ca restaurantul tău, nu ca al nostru.',
      },
      {
        title: 'Pornește de la meniul pe hârtie',
        text: 'Ai deja un meniu tipărit? Încarcă un PDF sau câteva fotografii, iar aplicația îl transformă în preparate și prețuri editabile - un meniu obișnuit încape în creditele gratuite primite la înregistrare.',
      },
      {
        title: 'AI-ul sugerează alergenii',
        text: 'Editezi un preparat? Lași AI-ul să îi citească numele și descrierea și să propună cei 14 alergeni UE pentru un credit. Tu verifici și confirmi - un avans, niciodată cuvântul final.',
      },
      {
        title: 'Clienții filtrează singuri meniul',
        text: 'La masă, clienții deschid un filtru, aleg ce vor să evite sau dieta de care au nevoie, iar preparatele nepotrivite se dau la o parte. Inclus din start, mereu activ, nimic de configurat.',
      },
      {
        title: 'Transformă clienții în recenzii Google',
        text: 'Adaugi linkul tău de recenzie Google, iar meniul se încheie cu un îndemn prietenos - clienții mulțumiți dau click și lasă o recenzie chiar la masă, cât timp mesele sunt încă proaspete în minte.',
      },
    ],
  },
  guests: {
    eyebrow: 'Clienții îl adoră',
    heading: {
      pre: 'Un meniu în care clienții tăi pot avea cu adevărat',
      highlight: 'încredere',
      post: '.',
    },
    paragraph:
      'Fotografii clare, prețuri corecte, alergeni marcați pe fiecare preparat și tot meniul în limba lor. Clienții comandă cu încredere - iar clienții încrezători comandă mai mult.',
    checklist: [
      'Se deschide în browser - nimic de instalat',
      'Etichete de alergeni pe fiecare preparat',
      'Un singur tap schimbă tot meniul în limba lor',
    ],
  },
  pricing: {
    eyebrow: 'Prețuri',
    heading: 'Începe gratuit. Rămâi gratuit dacă îți este suficient.',
    paragraph:
      'Primul tău meniu cu cinci preparate este gratuit - fără card bancar. Când meniul tău crește, treci la preparate nelimitate și mai multe credite AI, pentru descrieri, fotografii și traduceri de meniu. Toate cele șase limbi sunt incluse în orice plan. Anulezi oricând.',
    price: '€0',
    priceCaption: 'pentru a începe',
    cta: 'Creează-ți meniul gratuit',
    disclaimer: 'Fără card bancar. Fără limită de timp.',
  },
  faq: {
    eyebrow: 'Întrebări frecvente',
    heading: 'Ce întreabă proprietarii de restaurante despre SafestMenu.',
    items: [
      {
        question: 'Ce este SafestMenu și care este site-ul oficial?',
        answer:
          'SafestMenu este o platformă de meniuri cu cod QR pentru restaurante, cafenele și baruri. Site-ul oficial este safestmenu.com, iar proprietarii își gestionează meniurile pe app.safestmenu.com.',
      },
      {
        question: 'Cum funcționează un meniu cu cod QR SafestMenu?',
        answer:
          'Creezi și publici un meniu digital, apoi îi tipărești codul QR pentru mese. Clienții îl scanează cu camera telefonului, iar meniul actualizat se deschide în browser.',
      },
      {
        question: 'Clienții trebuie să instaleze o aplicație?',
        answer:
          'Nu. Clienții deschid meniul în browserul telefonului, fără să descarce o aplicație și fără să creeze un cont.',
      },
      {
        question: 'Pot schimba preparatele și prețurile după publicare?',
        answer:
          'Da. Editezi meniul de pe telefon sau computer și publici actualizarea. Același cod QR tipărit va afișa apoi cea mai recentă versiune.',
      },
      {
        question: 'Pot marca alergenii și oferi meniul în mai multe limbi?',
        answer:
          'Da. Poți marca preparatele cu cei 14 alergeni UE, clienții pot filtra preparatele și poți oferi meniul în engleză, germană, spaniolă, franceză, italiană și română.',
      },
      {
        question: 'Pot începe gratuit cu un meniu cu cod QR?',
        answer:
          'Da. Planul gratuit include un meniu cu până la cinci preparate, nu necesită card bancar și nu are limită de timp.',
      },
    ],
  },
  finalCta: {
    heading: {
      line1: 'Încearcă-l chiar acum -',
      highlight: 'scanează acest cod.',
    },
    paragraph:
      'Exact asta vor face clienții tăi la masă. Dacă îți place ce vezi, propriul tău meniu este la vreo cincisprezece minute distanță.',
    cta: 'Începe-ți meniul chiar azi',
  },
  jsonLd: {
    appDescription:
      'Meniuri cu cod QR pentru restaurante, cafenele și baruri. Creezi un meniu digital în câteva minute sau îți imporți meniul existent dintr-un PDF sau fotografii, actualizezi prețurile instant, schimbi meniurile după program, marchezi preparate ca indisponibile, cu etichete de alergeni, un filtru de alergeni pentru clienți și traducerea meniului în șase limbi.',
  },
};
